import { JsonObject, JsonProperty } from 'json2typescript';
import * as uuid from 'uuid';

import { BooleanInverterAdapter } from '../../client/helpers/serializer/adapters/boolean-inverter.adapter';
import { DocumentType, ManagerInterface, Nature } from '../../../core';
import { DocumentTypeAdapter } from '../../client/helpers/serializer/adapters/document-type.adapter';
import { Id } from './id';
import { NatureAdapter } from '../../client/helpers/serializer/adapters/nature.adapter';
import { StringAdapter } from '../../client/helpers/serializer/adapters/string.adapter';

@JsonObject('Manager')
export class Manager implements ManagerInterface {
    private readonly _id: Id = undefined;

    @JsonProperty('Name', StringAdapter, true)
    private _name: string = undefined;

    @JsonProperty('Surname', StringAdapter, true)
    private _surname: string = undefined;

    @JsonProperty('DocType', DocumentTypeAdapter, true)
    private _docType: DocumentType = undefined;

    @JsonProperty('IdNumber', StringAdapter, true)
    private _docId: string = undefined;

    @JsonProperty('Nationality', StringAdapter, true)
    private _citizenship: string = undefined;

    @JsonProperty('Type', NatureAdapter, true)
    private _nature: Nature = undefined;

    @JsonProperty('IsResident', Boolean, true)
    private _isResident: boolean = undefined;

    @JsonProperty('IsForeigner', BooleanInverterAdapter, true)
    private _isSpanish: boolean = undefined;

    @JsonProperty('LegalManagerCompany', StringAdapter, true)
    private _legalCompany: string = undefined;

    @JsonProperty('LegalManagerName', StringAdapter, true)
    private _legalName: string = undefined;

    @JsonProperty('LegalManagerSurname', StringAdapter, true)
    private _legalSurname: string = undefined;

    @JsonProperty('LegalManagerDocType', DocumentTypeAdapter, true)
    private _legalDocType: DocumentType = undefined;

    @JsonProperty('LegalManagerIdNumber', StringAdapter, true)
    private _legalDocId: string = undefined;

    @JsonProperty('LegalManagerIdNationality', StringAdapter, true)
    private _legalCitizenship: string = undefined;

    @JsonProperty('LegalManagerIsResident', Boolean, true)
    private _legalIsResident: boolean = undefined;

    @JsonProperty('LegalManagerIsForeigner', BooleanInverterAdapter, true)
    private _legalIsSpanish: boolean = undefined;


    constructor() {
        this._id = Id.fromString(uuid.v4());
        this._name = '';
        this._surname = '';
        this._legalName = '';
        this._legalSurname = '';
        this.nature = Nature.NATURAL;
        this.isSpanish = true;
        this.isResident = true;
    }

    get id(): Id {
        return this._id;
    }

    get name(): string {
        return this._name;
    }

    set name(value: string) {
        this._name = value;
    }

    get surname(): string {
        return this._surname;
    }

    set surname(value: string) {
        this._surname = value;
    }

    get fullName(): string {
        if (this.isLegalPerson) {
            return this.name;
        }

        return `${this.name} ${this.surname}`;
    }

    get docType(): DocumentType {
        return this._docType;
    }

    set docType(value: DocumentType) {
        this._docType = value;
    }

    get allowedDocTypes(): DocumentType[] {
        if (this.nature === Nature.COMPANY) {
            return [
                DocumentType.CIF
            ];
        } else if (this.isSpanish && this.isResident) {
            return [
                DocumentType.DNI
            ];
        } else if (this.isSpanish && !this.isResident) {
            return [
                DocumentType.DNI,
                DocumentType.NIF,
                DocumentType.NIE_NOT_RESIDENT
            ];
        } else if (!this.isSpanish && this.isResident) {
            return [
                DocumentType.NIE,
                DocumentType.PERMISSION,
                DocumentType.PASSPORT,
                DocumentType.PASSPORT_LATIN
            ];
        }

        return [
            DocumentType.NIE_NOT_RESIDENT,
            DocumentType.PASSPORT,
            DocumentType.PASSPORT_LATIN,
            DocumentType.IDENTIFICATION_CARD_NOT_RESIDENT
        ];
    }

    get docId(): string {
        return this._docId;
    }

    set docId(value: string) {
        this._docId = value ? value.toUpperCase() : value;
    }

    get citizenship(): string {
        if (!this._citizenship && this.isSpanish) {
            this._citizenship = 'ES';
        }

        return this._citizenship;
    }

    set citizenship(value: string) {
        this._citizenship = value;
    }

    get nature(): Nature {
        return this._nature;
    }

    set nature(value: Nature) {
        this._nature = value;

        if (this._nature === Nature.NATURAL) {
            this.legalIsResident = undefined;
            this.legalIsSpanish = undefined;
            this.legalCompany = undefined;
            this.legalName = undefined;
            this.legalSurname = undefined;
            this.legalDocType = undefined;
            this.legalDocId = undefined;
            this.legalCitizenship = undefined;
        }

        if (this._nature === Nature.COMPANY) {
            this.isResident = true;
            this.isSpanish = true;
            this.legalIsResident = true;
            this.legalIsSpanish = true;
        }

        this.docType = this.allowedDocTypes[0];
    }

    get isLegalPerson(): boolean {
        return this.nature === Nature.COMPANY;
    }

    get isResident(): boolean {
        return this._isResident;
    }

    set isResident(value: boolean) {
        this._isResident = value;

        this.docType = this.allowedDocTypes[0];
        this.docId = undefined;
    }

    get isSpanish(): boolean {
        return this._isSpanish;
    }

    set isSpanish(value: boolean) {
        this._isSpanish = value;

        if (this._isSpanish) {
            this.citizenship = 'ES';
        } else {
            this.citizenship = undefined;
        }

        this.docType = this.allowedDocTypes[0];
        this.docId = undefined;
    }

    get legalCompany(): string {
        return this._legalCompany;
    }

    set legalCompany(value: string) {
        this._legalCompany = value;
    }

    get legalName(): string {
        return this._legalName;
    }

    set legalName(value: string) {
        this._legalName = value;
    }

    get legalSurname(): string {
        return this._legalSurname;
    }

    set legalSurname(value: string) {
        this._legalSurname = value;
    }

    get legalFullName(): string {
        return `${this.legalName} ${this.legalSurname}`;
    }

    get legalDocType(): DocumentType {
        return this._legalDocType;
    }

    set legalDocType(value: DocumentType) {
        this._legalDocType = value;
    }

    get legalAllowedDocTypes(): DocumentType[] {
        if (this.legalIsSpanish && this.legalIsResident) {
            return [
                DocumentType.DNI
            ];
        } else if (this.legalIsSpanish && !this.legalIsResident) {
            return [
                DocumentType.DNI,
                DocumentType.NIF,
                DocumentType.NIE_NOT_RESIDENT
            ];
        } else if (!this.legalIsSpanish && this.legalIsResident) {
            return [
                DocumentType.NIE,
                DocumentType.PERMISSION,
                DocumentType.PASSPORT,
                DocumentType.PASSPORT_LATIN
            ];
        }

        return [
            DocumentType.NIE_NOT_RESIDENT,
            DocumentType.PASSPORT,
            DocumentType.PASSPORT_LATIN,
            DocumentType.IDENTIFICATION_CARD_NOT_RESIDENT
        ];
    }

    get legalDocId(): string {
        return this._legalDocId;
    }

    set legalDocId(value: string) {
        this._legalDocId = value ? value.toUpperCase() : value;
    }

    get legalCitizenship(): string {
        if (!this._legalCitizenship && this.legalIsSpanish) {
            this._legalCitizenship = 'ES';
        }

        return this._legalCitizenship;
    }

    set legalCitizenship(value: string) {
        this._legalCitizenship = value;
    }

    get legalIsResident(): boolean {
        return this._legalIsResident;
    }

    set legalIsResident(value: boolean) {
        this._legalIsResident = value;

        this.legalDocType = this.legalAllowedDocTypes[0];
        this.legalDocId = undefined;
    }

    get legalIsSpanish(): boolean {
        return this._legalIsSpanish;
    }

    set legalIsSpanish(value: boolean) {
        this._legalIsSpanish = value;

        if (this._legalIsSpanish) {
            this.legalCitizenship = 'ES';
        } else {
            this.legalCitizenship = undefined;
        }

        this.legalDocType = this.legalAllowedDocTypes[0];
        this.legalDocId = undefined;
    }

    isComplete(): boolean {
        if (this.nature === Nature.NATURAL) {
            return !!this.nature
                && !!this.name
                && !!this.surname
                && !!this.docId
                && !!this.citizenship;
        } else {
            return !!this.nature
                && !!this.name
                && !!this.docId
                && !!this.citizenship
                && !!this.legalCompany
                && !!this.legalName
                && !!this.legalSurname
                && !!this.legalDocId
                && !!this.legalCitizenship;
        }
    }
}
